import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Clock, ScanLine, MapPin, ExternalLink, AlertCircle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Lists active clock-in branches and lets the employee jump straight into the
 * public scanner page (/clock/:qr_token) for the location they're at.
 *
 * Renders as a list of branch cards. Tapping a card opens the public scanner
 * page in a new tab so the camera can run without losing the portal session.
 */
const PortalClockIn = () => {
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Authenticated portal endpoint — respects both hide_from_portal and
        // per-station department allowlists (portal_visible_departments).
        const token = localStorage.getItem('portal_token');
        const r = await axios.get(`${API}/api/employee-portal/clock-stations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setBranches(r.data || []);
      } catch (err) {
        if (!cancelled) {
          toast({
            title: 'Could not load locations',
            description: err?.response?.data?.detail || err?.message || 'Please retry shortly.',
            variant: 'destructive',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openScanner = (branch) => {
    const url = `${window.location.origin}/clock/${branch.qr_token}`;
    // Open in a new tab — keeps the portal session alive and lets the
    // camera/QR scanner take the full screen on the device.
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-3 p-4 max-w-3xl mx-auto" data-testid="portal-clock-in">
      <button
        onClick={() => navigate('/employee-portal')}
        className="text-sky-600 text-sm flex items-center gap-1"
        data-testid="back-to-home"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </button>

      {/* Hero */}
      <Card className="border-0 bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-md">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center">
              <ScanLine className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-bold">Clock In / Out</h2>
              <p className="text-xs text-sky-100/90 mt-0.5">
                Pick the location you're at to open the scanner.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 text-center text-sm text-gray-400">Loading locations…</CardContent>
        </Card>
      ) : branches.length === 0 ? (
        <Card className="border-0 shadow-sm border-l-4 border-l-amber-400">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-gray-800">No active locations</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Ask your manager to activate a clock-in station, then refresh this page.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          <p className="text-[11px] uppercase tracking-wider text-gray-400 px-1">
            {branches.length === 1 ? 'Your location' : `Choose a location (${branches.length})`}
          </p>
          {branches.map((b) => (
            <Card
              key={b.id}
              className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer active:scale-[0.99]"
              onClick={() => openScanner(b)}
              data-testid={`clock-branch-${b.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                    <MapPin className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-semibold text-gray-800 truncate">{b.name}</p>
                      <Badge variant="outline" className="text-[9px] uppercase tracking-wide border-emerald-200 text-emerald-700 bg-emerald-50">Active</Badge>
                    </div>
                    {b.address ? (
                      <p className="text-[11px] text-gray-500 truncate mt-0.5">{b.address}</p>
                    ) : null}
                  </div>
                  <Button
                    size="sm"
                    className="h-8 bg-sky-600 hover:bg-sky-700"
                    data-testid={`clock-branch-open-${b.id}`}
                    onClick={(e) => { e.stopPropagation(); openScanner(b); }}
                  >
                    Open <ExternalLink className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Helper card */}
      <Card className="border-0 bg-gray-50 shadow-none">
        <CardContent className="p-3">
          <div className="flex items-start gap-2">
            <Clock className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
            <p className="text-[11px] text-gray-500 leading-relaxed">
              The scanner opens in a new tab so it can use your device camera.
              Make sure you've set your <span className="font-semibold">4-digit Clock-In PIN</span> from
              the menu before using the scanner.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PortalClockIn;
